/**
 * TopologyPage — Live network topology visualizer for operators (DAU-friendly).
 *
 * Shows the full layered stack:
 *   Internet ──► vmbr1 ──► Egress-GW
 *                              │
 *   vmbr0 / Backend-LAN ──VRF leak──► EVPN VRF
 *     ├─ MariaDB (DB-Pools)              │
 *     ├─ Garage S3 (S3-Pools)            ├─► Service-Zone (cmsvc0)
 *     └─ PVE Control + Worker Nodes      │      └─ Redis / Collabora / …
 *                                         └─► Tenant-Zones (cmtnt0+)
 *                                                └─ Nextcloud VMs per cloud
 *
 * Auto-refreshes every 30 s, click on any box for details.
 */
import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import {
    Globe2,
    ShieldHalf,
    Server,
    Database,
    HardDrive,
    Network,
    Boxes,
    Cpu,
    RefreshCw,
    Eye,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Copy,
    Terminal,
    AlertTriangle,
} from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

// ---------- Tiny presentational primitives ----------
const DRIFT_LABEL = {
    in_sync: { text: "✓", title: "in sync with Proxmox", color: "text-emerald-400" },
    missing_in_proxmox: { text: "↯", title: "in DB but missing in Proxmox — re-apply", color: "text-red-400" },
    config_mismatch: { text: "≠", title: "config differs — Proxmox has different values", color: "text-amber-400" },
    unknown: { text: "?", title: "unknown — Proxmox not queried", color: "text-zinc-500" },
};

const DriftBadge = ({ drift }) => {
    if (!drift || drift === "unknown") return null;
    const info = DRIFT_LABEL[drift] || DRIFT_LABEL.unknown;
    return (
        <span
            className={`text-[11px] font-mono ${info.color}`}
            title={info.title}
            data-testid={`drift-badge-${drift}`}
        >
            {info.text}
        </span>
    );
};

const Box = ({ icon: Icon, title, sub, status, drift, onClick, testid, color = "zinc" }) => {
    const palette = {
        zinc: "border-zinc-700 hover:border-zinc-500 bg-zinc-950",
        blue: "border-[#CCFF00]/60 hover:border-[#CCFF00] bg-[#CCFF00]/5",
        green: "border-emerald-700 hover:border-emerald-500 bg-emerald-950/30",
        amber: "border-amber-700 hover:border-amber-500 bg-amber-950/30",
        red: "border-red-700 hover:border-red-500 bg-red-950/30",
        purple: "border-purple-700 hover:border-purple-500 bg-purple-950/30",
    }[color];
    // Drift-overrides the border colour for visual urgency
    const driftClass =
        drift === "missing_in_proxmox" ? "!border-red-500 ring-1 ring-red-500/40"
        : drift === "config_mismatch" ? "!border-amber-500 ring-1 ring-amber-500/40"
        : "";
    return (
        <div
            data-testid={testid}
            onClick={onClick}
            className={`relative cursor-pointer rounded-md border-2 ${palette} ${driftClass} p-3 transition-all hover:shadow-lg hover:shadow-[#CCFF00]/10`}
        >
            <div className="flex items-start gap-2">
                {Icon && <Icon size={16} className="mt-0.5 shrink-0 opacity-80" />}
                <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold leading-tight truncate flex items-center gap-1.5">
                        {title}
                        <DriftBadge drift={drift} />
                    </div>
                    {sub && (
                        <div className="font-mono text-[11px] text-zinc-400 leading-tight truncate">
                            {sub}
                        </div>
                    )}
                </div>
                {status && <StatusDot status={status} />}
            </div>
        </div>
    );
};

const StatusDot = ({ status }) => {
    const map = {
        active: "bg-emerald-500",
        online: "bg-emerald-500",
        ready: "bg-emerald-500",
        running: "bg-emerald-500",
        healthy: "bg-emerald-500",
        provisioning: "bg-amber-500 animate-pulse",
        installing: "bg-amber-500 animate-pulse",
        pending: "bg-amber-500 animate-pulse",
        unknown: "bg-zinc-500",
        offline: "bg-red-500",
        error: "bg-red-500",
        down: "bg-red-500",
        degraded: "bg-amber-500",
    };
    return (
        <div
            className={`w-2 h-2 rounded-full mt-1 shrink-0 ${
                map[status] || "bg-zinc-500"
            }`}
            title={status}
        />
    );
};

// ---- Tenant-Zone: Health-Summary + Problemfälle + Suche (skaliert auf
//      hunderte Tenants) ----------------------------------------------------
const CHIP_CLS = {
    emerald: "text-emerald-300 border-emerald-800 bg-emerald-950/30",
    amber: "text-amber-300 border-amber-800 bg-amber-950/30",
    red: "text-red-300 border-red-800 bg-red-950/30",
    zinc: "text-zinc-400 border-zinc-800 bg-zinc-950/40",
};
const Chip = ({ color, n, children, testid }) => (
    <span data-testid={testid} className={`px-1.5 py-0.5 rounded border ${CHIP_CLS[color] || CHIP_CLS.zinc}`}>
        {n || 0} {children}
    </span>
);
const TENANT_BOX_COLOR = { healthy: "green", degraded: "amber", pending: "amber", down: "red", unknown: "zinc" };

function TenantZones({ tenants, summary, onDetail }) {
    const [q, setQ] = useState("");
    const [showAll, setShowAll] = useState(false);
    const s = summary || {};
    const query = q.trim().toLowerCase();
    const problems = tenants.filter((t) => ["down", "degraded", "unknown"].includes(t.health));
    let list;
    if (query) {
        list = tenants.filter((t) =>
            (t.cloud_name || "").toLowerCase().includes(query)
            || (t.cloud_id || "").toLowerCase().includes(query)
            || (t.cidr || "").toLowerCase().includes(query));
    } else {
        list = showAll ? tenants : problems;
    }
    return (
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-3 text-xs font-mono" data-testid="topo-tenant-summary">
                <span className="text-zinc-400">{s.total || 0} Tenants:</span>
                <Chip color="emerald" n={s.healthy} testid="topo-tenant-sum-healthy">healthy</Chip>
                <Chip color="amber" n={s.degraded} testid="topo-tenant-sum-degraded">degraded</Chip>
                <Chip color="red" n={s.down} testid="topo-tenant-sum-down">down</Chip>
                <Chip color="amber" n={s.pending} testid="topo-tenant-sum-pending">pending</Chip>
                <Chip color="zinc" n={s.unknown} testid="topo-tenant-sum-unknown">unknown</Chip>
            </div>
            <div className="flex gap-2 mb-3">
                <input value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search tenant (name / cloud ID / subnet)…"
                    className="cm-input text-xs flex-1" data-testid="topo-tenant-search" />
                <button onClick={() => setShowAll((v) => !v)} disabled={!!query}
                    className="cm-btn-ghost text-xs px-2 whitespace-nowrap disabled:opacity-40"
                    data-testid="topo-tenant-toggle-all">
                    {showAll ? "Only problems" : `Show all (${tenants.length})`}
                </button>
            </div>
            {list.length === 0 ? (
                <div className="text-xs text-zinc-500 italic pl-6" data-testid="topo-tenant-empty">
                    {query ? "No tenant found."
                        : ((s.total || 0) === 0
                            ? "No tenants provisioned."
                            : problems.length === 0
                                ? "All tenants healthy ✓ — the button at top right shows the full list."
                                : "—")}
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 pl-6">
                    {list.slice(0, 200).map((t) => (
                        <Box key={t.cidr || t.cloud_id} icon={Boxes}
                            title={t.cloud_name || `cloud ${(t.cloud_id || "").slice(0, 8)}…`}
                            sub={`${t.web_count || 0}w/${t.worker_count || 0}wk · ${t.cidr || "?"}${t.region ? " · " + t.region : ""}`}
                            color={TENANT_BOX_COLOR[t.health] || "purple"}
                            status={t.health}
                            testid={`topo-tnt-${t.cloud_id}`}
                            onClick={() => onDetail({ title: `Tenant — ${t.cloud_name || t.cloud_id}`, data: t })} />
                    ))}
                </div>
            )}
            {list.length > 200 && (
                <div className="text-[11px] text-zinc-500 pl-6 mt-2">
                    … {list.length - 200} more — use search
                </div>
            )}
        </div>
    );
}

const Lane = ({ label, sublabel, color = "zinc", children }) => {
    const bg = {
        zinc: "bg-zinc-950/40 border-zinc-800",
        blue: "bg-[#CCFF00]/5 border-[#CCFF00]/30",
        purple: "bg-purple-950/20 border-purple-800/50",
        amber: "bg-amber-950/20 border-amber-800/50",
        green: "bg-emerald-950/20 border-emerald-800/50",
    }[color];
    return (
        <div
            className={`relative rounded-md border-2 border-dashed ${bg} p-4 mb-4`}
        >
            <div className="absolute -top-3 left-3 bg-cm-bg px-2 text-xs uppercase tracking-wider text-zinc-300 font-semibold">
                {label}
            </div>
            {sublabel && (
                <div className="text-[11px] font-mono text-zinc-500 mb-3 -mt-1">
                    {sublabel}
                </div>
            )}
            {children}
        </div>
    );
};

const Arrow = ({ label }) => (
    <div className="flex flex-col items-center my-2">
        <div className="w-px h-3 bg-zinc-600" />
        <div className="text-[11px] font-mono text-zinc-500 px-2 py-0.5 rounded border border-zinc-800 bg-zinc-950">
            {label}
        </div>
        <div className="w-px h-3 bg-zinc-600" />
        <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-zinc-600" />
    </div>
);

// ---------- FRR Default-Route Helper Card ----------
const FrrSnippetCard = ({ clusterId, evpnZone, defaultEgressIp }) => {
    const [egressIp, setEgressIp] = useState(defaultEgressIp || "10.16.0.2");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    const generate = async () => {
        setBusy(true);
        setErr(null);
        try {
            const r = await api.post(
                "/api/admin/infra/sdn/frr-default-route-snippet",
                {
                    cluster_id: clusterId,
                    zone_name: evpnZone.zone,
                    egress_gw_ip: egressIp,
                }
            );
            setResult(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || String(e));
        } finally {
            setBusy(false);
        }
    };

    const copy = (text) => {
        if (navigator.clipboard) navigator.clipboard.writeText(text);
    };

    return (
        <div className="cm-card p-4 border-[#CCFF00]/30 bg-[#CCFF00]/5" data-testid="frr-snippet-card">
            <div className="cm-h3 mb-1 flex items-center gap-2">
                <Terminal size={14} /> FRR Default-Route helper
            </div>
            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
                Proxmox SDN has no UI for VRF-static-routes. Generate the
                FRR snippet here, then run the script on every PVE exit-node.
                Without this, EVPN VMs reach the backend-LAN but NOT the Internet.
            </p>
            {!result && (
                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <label className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                            Egress-GW IP in service-zone
                        </label>
                        <input
                            type="text"
                            value={egressIp}
                            onChange={(e) => setEgressIp(e.target.value)}
                            className="cm-input w-full font-mono text-xs"
                            placeholder="10.16.0.2"
                            data-testid="frr-snippet-egress-ip"
                        />
                    </div>
                    <button
                        onClick={generate}
                        disabled={busy || !egressIp}
                        className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                        data-testid="frr-snippet-generate"
                    >
                        {busy ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <Terminal size={12} />
                        )}
                        Generate
                    </button>
                </div>
            )}
            {err && (
                <div className="mt-2 text-red-400 text-xs font-mono bg-red-950/30 border border-red-900 p-2 rounded">
                    {err}
                </div>
            )}
            {result && (
                <div className="space-y-3">
                    <div className="text-xs text-emerald-300 font-mono">
                        VRF: <code>{result.vrf_name}</code> · default-route via{" "}
                        <code>{result.egress_gw_ip}</code>
                    </div>
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <div className="text-[11px] uppercase tracking-wider text-zinc-400">
                                Apply script (run as root on every exit-node)
                            </div>
                            <button
                                onClick={() => copy(result.apply_script)}
                                className="text-[11px] text-cm-brand hover:underline flex items-center gap-1"
                                data-testid="frr-snippet-copy-script"
                            >
                                <Copy size={10} /> copy
                            </button>
                        </div>
                        <pre className="font-mono text-[11px] text-zinc-200 bg-zinc-950 border border-zinc-800 rounded p-2 max-h-72 overflow-auto whitespace-pre">
                            {result.apply_script}
                        </pre>
                    </div>
                    <div className="text-xs text-zinc-300 bg-zinc-950 border border-zinc-800 rounded p-2 leading-relaxed">
                        {result.instructions}
                    </div>
                    <button
                        onClick={() => setResult(null)}
                        className="cm-btn-ghost text-xs px-2 py-1"
                        data-testid="frr-snippet-reset"
                    >
                        Generate again
                    </button>
                </div>
            )}
        </div>
    );
};


// ---------- Drift Banner ----------
const DriftBanner = ({ cluster }) => {
    const driftSummary = cluster.sdn.drift_summary || "skipped";
    const missing = cluster.sdn.missing_in_db || {};
    const missingTotal =
        (missing.controllers?.length || 0)
        + (missing.zones?.length || 0)
        + (missing.vnets?.length || 0);

    if (driftSummary === "in_sync") {
        return (
            <div
                className="cm-card p-3 border-emerald-800 bg-emerald-950/20 text-emerald-200 flex items-center gap-2 text-xs"
                data-testid="drift-banner-ok"
            >
                <CheckCircle2 size={14} /> CM-DB and Proxmox SDN are <strong>in sync</strong>.
            </div>
        );
    }
    if (driftSummary === "skipped" || driftSummary.startsWith("unavailable")) {
        return (
            <div
                className="cm-card p-3 border-zinc-700 bg-zinc-950/40 text-zinc-300 flex items-center gap-2 text-xs"
                data-testid="drift-banner-skipped"
            >
                <AlertCircle size={14} className="text-zinc-500" />
                Drift check: <span className="font-mono">{driftSummary}</span>
            </div>
        );
    }
    return (
        <div
            className="cm-card p-3 border-amber-700 bg-amber-950/30 text-amber-100 flex items-start gap-2 text-xs"
            data-testid="drift-banner-drift"
        >
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-400" />
            <div className="flex-1">
                <div>
                    <strong>SDN drift detected</strong> — {driftSummary}.
                </div>
                {missingTotal > 0 && (
                    <div className="mt-1 text-xs text-amber-300">
                        Proxmox has {missingTotal} object(s) CM doesn&apos;t track:
                        {missing.controllers?.length > 0 && (
                            <span> controllers=[{missing.controllers.join(", ")}]</span>
                        )}
                        {missing.zones?.length > 0 && (
                            <span> zones=[{missing.zones.join(", ")}]</span>
                        )}
                        {missing.vnets?.length > 0 && (
                            <span> vnets=[{missing.vnets.slice(0, 6).join(", ")}
                                {missing.vnets.length > 6 ? `, +${missing.vnets.length - 6} more` : ""}]
                            </span>
                        )}
                    </div>
                )}
                <div className="mt-1 text-xs text-amber-300/80">
                    Boxes outlined in <span className="text-red-400">red</span> are
                    missing in Proxmox; <span className="text-amber-300">amber</span>
                    {" "}boxes have config-mismatch. Click a box for details.
                </div>
            </div>
        </div>
    );
};


// ---------- Detail Drawer ----------
const DetailDrawer = ({ open, onClose, title, data }) => {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="topology-detail-drawer"
        >
            <div
                className="w-[420px] bg-cm-bg border-l-2 border-zinc-800 h-full p-5 overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-4">
                    <div className="cm-h3">{title}</div>
                    <button
                        onClick={onClose}
                        className="cm-btn-ghost text-xs px-2 py-1"
                        data-testid="topology-detail-close"
                    >
                        Close
                    </button>
                </div>
                <pre className="font-mono text-xs text-zinc-300 whitespace-pre-wrap break-all bg-zinc-950 border border-zinc-800 rounded p-3">
                    {JSON.stringify(data, null, 2)}
                </pre>
            </div>
        </div>
    );
};

// ---------- Main page ----------
export default function TopologyPage() {
    const { data, error, isLoading, mutate } = useSWR(
        "/api/admin/infra/topology",
        fetcher,
        { refreshInterval: 30000 }
    );
    const [detail, setDetail] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [cleanupBusy, setCleanupBusy] = useState(false);
    const [cleanupResult, setCleanupResult] = useState(null);

    useEffect(() => {
        if (!autoRefresh) return;
        const t = setInterval(() => mutate(), 30000);
        return () => clearInterval(t);
    }, [autoRefresh, mutate]);

    const handleCleanupUntracked = async (cluster) => {
        const missing = cluster.sdn.missing_in_db || {};
        const total =
            (missing.vnets?.length || 0)
            + (missing.zones?.length || 0)
            + (missing.controllers?.length || 0);
        if (!total) return;
        if (!window.confirm(
            `Permanently delete ${total} untracked SDN object(s) from Proxmox?\n\n` +
            `VNets:        ${missing.vnets?.length || 0}\n` +
            `Zones:        ${missing.zones?.length || 0}\n` +
            `Controllers:  ${missing.controllers?.length || 0}\n\n` +
            `VMs attached to these VNets will be disconnected. Continue?`
        )) {
            return;
        }
        setCleanupBusy(true);
        setCleanupResult(null);
        try {
            const r = await api.post("/api/admin/infra/sdn/cleanup-untracked", {
                cluster_id: cluster.id,
                vnets: missing.vnets || [],
                zones: missing.zones || [],
                controllers: missing.controllers || [],
            });
            setCleanupResult(r.data);
            await mutate();
        } catch (e) {
            setCleanupResult({ error: e?.response?.data?.detail || String(e) });
        } finally {
            setCleanupBusy(false);
        }
    };

    const handleDeleteZone = async (cluster, zoneName) => {
        if (!window.confirm(
            `Delete SDN zone "${zoneName}" from both CM-DB and Proxmox?\n\n` +
            `Any VNets in this zone will ALSO be deleted (cascade). ` +
            `Attached VMs will lose connectivity. Continue?`
        )) {
            return;
        }
        try {
            await api.delete(
                `/api/admin/infra/sdn/zones/${encodeURIComponent(zoneName)}`,
                { params: { cluster_id: cluster.id, cascade: true, also_proxmox: true } }
            );
            await mutate();
        } catch (e) {
            alert("Delete failed: " + (e?.response?.data?.detail || String(e)));
        }
    };

    const handleCleanupServiceVmOrphans = async () => {
        if (!window.confirm(
            "Cleanup orphan service-VM rows (no vmid, status=failed/provisioning, older than 5min)?\n\n" +
            "Proxmox VMs are NOT touched — this only removes leftover DB placeholders that confuse the topology view."
        )) {
            return;
        }
        setCleanupBusy(true);
        setCleanupResult(null);
        try {
            const r = await api.post("/api/admin/infra/service-vms/cleanup-orphans");
            setCleanupResult(r.data);
            await mutate();
        } catch (e) {
            setCleanupResult({ error: e?.response?.data?.detail || String(e) });
        } finally {
            setCleanupBusy(false);
        }
    };

    // Full reconcile against the live Proxmox cluster — covers two cases
    // the orphan-cleanup above misses:
    //   • DB rows WITH vmid but the VM no longer exists in Proxmox
    //     (deleted in PVE UI, never finished cloning, etc.) → "ghost rows"
    //   • VMs in Proxmox with a CM-managed name (`cm-…`) but no DB row
    //     referencing their VMID → "untracked Proxmox VMs"
    // Always runs as a dry-run first; the operator picks which categories
    // to actually delete.
    const reconcile = useSWR(
        "/api/admin/infra/service-vms/reconcile",
        fetcher,
        { refreshInterval: 0 },
    );
    const reconcileReport = reconcile.data;

    const runReconcileApply = async ({ ghosts, untrackedPmx, stuck }) => {
        const parts = [];
        if (ghosts) parts.push(`${reconcileReport?.summary?.ghost_db_rows || 0} ghost DB row(s)`);
        if (untrackedPmx) parts.push(`${reconcileReport?.summary?.untracked_proxmox_vms || 0} untracked Proxmox VM(s) (will be destroyed!)`);
        if (stuck) parts.push(`${reconcileReport?.summary?.stuck_no_vmid || 0} stuck row(s) without vmid`);
        if (!parts.length) return;
        if (!window.confirm(
            `Apply reconcile cleanup?\n\n• ${parts.join("\n• ")}\n\n` +
            (untrackedPmx ? "WARNING: Untracked Proxmox VMs will be permanently destroyed.\n\n" : "") +
            "Continue?"
        )) return;
        setCleanupBusy(true);
        setCleanupResult(null);
        try {
            const r = await api.post("/api/admin/infra/service-vms/reconcile", {
                delete_ghost_db_rows: !!ghosts,
                destroy_untracked_proxmox: !!untrackedPmx,
                delete_stuck_no_vmid: !!stuck,
            });
            setCleanupResult(r.data);
            await Promise.all([mutate(), reconcile.mutate()]);
        } catch (e) {
            setCleanupResult({ error: e?.response?.data?.detail || String(e) });
        } finally {
            setCleanupBusy(false);
        }
    };

    if (isLoading) {
        return (
            <div className="p-8 flex items-center gap-3 text-zinc-400">
                <Loader2 className="animate-spin" size={16} />
                Loading topology…
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-8 text-red-400 flex items-center gap-2">
                <AlertCircle size={16} /> Failed to load topology: {String(error)}
            </div>
        );
    }
    if (!data || !data.clusters?.length) {
        return (
            <div className="p-8 text-zinc-400 cm-card">
                No Proxmox cluster registered yet. Add one in{" "}
                <a className="text-cm-brand underline" href="/infrastructure">
                    Infrastructure
                </a>{" "}
                first.
            </div>
        );
    }

    const cluster = data.clusters[0]; // single-cluster pretty layout
    const controlNodes = cluster.nodes.filter((n) => n.role === "control");
    const workerNodes = cluster.nodes.filter((n) => n.role === "worker");
    const evpnZone = cluster.sdn.zones.find((z) => z.type === "evpn");
    const evpnReady = !!evpnZone;
    const serviceVnet = cluster.sdn.vnets.find((v) => v.kind === "service-zone");
    const tenantVnets = cluster.sdn.vnets.filter((v) => v.kind !== "service-zone");
    // Detect orphan placeholder service-VMs (no vmid AND status ∈ {failed,
    // provisioning, error, unhealthy}). They render as confusing
    // "<kind>-null" cards in the service-zone section.
    const orphanServiceVms = (cluster.service_vms || []).filter(
        (v) => !v.vmid && ["failed", "provisioning", "error", "unhealthy"].includes(v.status)
    );

    return (
        <div className="space-y-6 p-6" data-testid="topology-page">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="cm-h1">Network Topology</h1>
                    <p className="text-sm text-zinc-400 mt-1">
                        Live operator view — click any node to inspect.{" "}
                        <span className="text-zinc-500 font-mono">
                            (updated {data.generated_at?.slice(11, 19)} UTC)
                        </span>
                    </p>
                </div>
                <div className="flex gap-2 items-center">
                    <label className="text-xs text-zinc-400 flex items-center gap-1.5">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            data-testid="topology-autorefresh"
                        />
                        Auto-refresh 30s
                    </label>
                    <button
                        onClick={() => mutate()}
                        className="cm-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
                        data-testid="topology-refresh"
                    >
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>
            </div>

            {/* EVPN-Status Banner */}
            {!evpnReady && (
                <div
                    className="cm-card p-3 border-amber-700 bg-amber-950/30 text-amber-200 flex items-start gap-2 text-xs"
                    data-testid="evpn-status-warning"
                >
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <div>
                        <strong>EVPN not bootstrapped yet.</strong> Service- and
                        Tenant-VMs cannot reach the backend LAN (10.101.0.0/24)
                        natively. Go to{" "}
                        <a className="text-cm-brand underline" href="/infrastructure">
                            Infrastructure → SDN
                        </a>{" "}
                        and click <em>Bootstrap EVPN</em>.
                    </div>
                </div>
            )}

            {/* Drift Banner — appears below EVPN-status, includes cleanup action */}
            <DriftBanner cluster={cluster} />
            {(cluster.sdn.missing_in_db?.vnets?.length > 0
              || cluster.sdn.missing_in_db?.zones?.length > 0
              || cluster.sdn.missing_in_db?.controllers?.length > 0) && (
                <div className="cm-card p-3 border-amber-700 bg-amber-950/10 flex items-center justify-between gap-3 text-xs">
                    <div className="text-amber-200">
                        Untracked objects detected in Proxmox. Use{" "}
                        <strong>Cleanup untracked</strong> to remove legacy
                        VXLAN-zones / orphan VNets so the topology matches CM.
                    </div>
                    <button
                        onClick={() => handleCleanupUntracked(cluster)}
                        disabled={cleanupBusy}
                        className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50 shrink-0"
                        data-testid="topology-cleanup-untracked"
                    >
                        {cleanupBusy ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                        Cleanup untracked
                    </button>
                </div>
            )}
            {orphanServiceVms.length > 0 && (
                <div className="cm-card p-3 border-amber-700 bg-amber-950/10 flex items-center justify-between gap-3 text-xs"
                    data-testid="topology-orphan-svc-vms-banner">
                    <div className="text-amber-200">
                        <strong>{orphanServiceVms.length} orphan service-VM row(s)</strong>{" "}
                        detected (no vmid, status={orphanServiceVms.map((v) => v.status).join("/")}).
                        These show as <code className="font-mono">{orphanServiceVms.map((v) => `${v.kind}-null`).join(", ")}</code> below.
                        Click <strong>Cleanup orphans</strong> to remove them — only DB placeholders, no Proxmox VMs will be touched.
                    </div>
                    <button
                        onClick={handleCleanupServiceVmOrphans}
                        disabled={cleanupBusy}
                        className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50 shrink-0"
                        data-testid="topology-cleanup-orphan-svc-vms"
                    >
                        {cleanupBusy ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                        Cleanup orphans
                    </button>
                </div>
            )}
            {reconcileReport && (
                reconcileReport.summary.ghost_db_rows > 0
                || reconcileReport.summary.untracked_proxmox_vms > 0
                || reconcileReport.summary.stuck_no_vmid > 0
            ) && (
                <div className="cm-card p-3 border-red-700 bg-red-950/20 text-xs space-y-2"
                    data-testid="topology-reconcile-banner">
                    <div className="text-red-200 font-semibold flex items-center gap-2">
                        <AlertTriangle size={14} /> Service-VM ↔ Proxmox reconciliation drift
                    </div>
                    {reconcileReport.summary.ghost_db_rows > 0 && (
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-red-100">
                                <strong>{reconcileReport.summary.ghost_db_rows}</strong>{" "}
                                <span className="font-mono text-[11px] bg-red-900/40 px-1.5 py-0.5 rounded">ghost_db_rows</span>{" "}
                                — DB row(s) point at a vmid Proxmox does not have:{" "}
                                <span className="font-mono text-[11px]">
                                    {reconcileReport.ghost_db_rows
                                        .map((g) => `${g.kind}#${g.vmid}`).join(", ")}
                                </span>
                            </div>
                            <button
                                onClick={() => runReconcileApply({ ghosts: true })}
                                disabled={cleanupBusy}
                                className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50 shrink-0"
                                data-testid="topology-reconcile-drop-ghosts"
                            >
                                {cleanupBusy ? <Loader2 size={12} className="animate-spin" /> : null}
                                Drop ghost rows
                            </button>
                        </div>
                    )}
                    {reconcileReport.summary.untracked_proxmox_vms > 0 && (
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-red-100">
                                <strong>{reconcileReport.summary.untracked_proxmox_vms}</strong>{" "}
                                <span className="font-mono text-[11px] bg-red-900/40 px-1.5 py-0.5 rounded">untracked_proxmox_vms</span>{" "}
                                — Proxmox VM(s) matching <code>cm-*</code> with no DB row:{" "}
                                <span className="font-mono text-[11px]">
                                    {reconcileReport.untracked_proxmox_vms
                                        .map((u) => `${u.name}#${u.vmid}@${u.node}`).join(", ")}
                                </span>
                            </div>
                            <button
                                onClick={() => runReconcileApply({ untrackedPmx: true })}
                                disabled={cleanupBusy}
                                className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50 shrink-0 border border-red-700 text-red-200"
                                data-testid="topology-reconcile-destroy-untracked"
                            >
                                Destroy in Proxmox
                            </button>
                        </div>
                    )}
                    {reconcileReport.summary.stuck_no_vmid > 0 && (
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-red-100">
                                <strong>{reconcileReport.summary.stuck_no_vmid}</strong>{" "}
                                <span className="font-mono text-[11px] bg-red-900/40 px-1.5 py-0.5 rounded">stuck_no_vmid</span>{" "}
                                — DB row(s) older than 5min with no vmid.
                            </div>
                            <button
                                onClick={() => runReconcileApply({ stuck: true })}
                                disabled={cleanupBusy}
                                className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50 shrink-0"
                                data-testid="topology-reconcile-drop-stuck"
                            >
                                Drop stuck rows
                            </button>
                        </div>
                    )}
                    <div className="flex justify-end pt-1 border-t border-red-900/40">
                        <button
                            onClick={() => reconcile.mutate()}
                            className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1"
                            data-testid="topology-reconcile-refresh"
                        >
                            <RefreshCw size={10} /> Re-check
                        </button>
                    </div>
                </div>
            )}
            {cleanupResult && (
                <div className="cm-card p-3 border-emerald-700 bg-emerald-950/20 text-xs" data-testid="cleanup-result">
                    <div className="font-semibold text-emerald-300 mb-1">
                        Cleanup complete
                    </div>
                    <pre className="font-mono text-[11px] text-zinc-300 max-h-40 overflow-auto bg-zinc-950 p-2 rounded">
                        {JSON.stringify(cleanupResult, null, 2)}
                    </pre>
                    <button
                        onClick={() => setCleanupResult(null)}
                        className="cm-btn-ghost text-xs px-2 py-1 mt-2"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* FRR Default-Route helper — only relevant when EVPN is active */}
            {evpnReady && (
                <FrrSnippetCard
                    clusterId={cluster.id}
                    evpnZone={evpnZone}
                    defaultEgressIp={cluster.egress_gateways[0]?.service_zone_ip || "10.16.0.2"}
                />
            )}

            {/* ===== LAYER 1: Internet ===== */}
            <Lane label="Internet" color="zinc">
                <div className="flex justify-center">
                    <Box
                        icon={Globe2}
                        title="Public Internet"
                        sub="0.0.0.0/0"
                        color="zinc"
                        testid="topo-internet"
                        onClick={() =>
                            setDetail({
                                title: "Public Internet",
                                data: { description: "External — reached via Egress-GW NAT", bridge: cluster.public_bridge },
                            })
                        }
                    />
                </div>
            </Lane>

            <Arrow label={`bridge ${cluster.public_bridge} (public, control-nodes only)`} />

            {/* ===== LAYER 2: Egress GWs ===== */}
            <Lane
                label="Egress Gateway"
                sublabel={`Dual-NIC VM(s): eth0 → ${cluster.public_bridge} (public), eth1 → cmsvc0 (service-zone)`}
                color="amber"
            >
                {cluster.egress_gateways.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">
                        No egress-gw VM yet. Provision one via{" "}
                        <a className="text-cm-brand underline" href="/infrastructure">
                            Infrastructure → Service VMs
                        </a>
                        .
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {cluster.egress_gateways.map((g) => (
                            <Box
                                key={g.id}
                                icon={ShieldHalf}
                                title={g.hostname || `egress-${g.vmid}`}
                                sub={`pub=${g.public_ip || "?"} • svc=${g.service_zone_ip || "?"}`}
                                color="amber"
                                status={g.status}
                                testid={`topo-egress-${g.vmid}`}
                                onClick={() =>
                                    setDetail({ title: `Egress GW — ${g.hostname}`, data: g })
                                }
                            />
                        ))}
                    </div>
                )}
            </Lane>

            <Arrow label={`VRF default route (0.0.0.0/0) — FRR static`} />

            {/* ===== LAYER 3: Backend-LAN + Underlay ===== */}
            <Lane
                label={`Backend LAN  ─  ${cluster.underlay_bridge}  ─  ${cluster.backend_lan_cidr}`}
                sublabel="Physical service network — PVE hosts + MariaDB + S3 live here. CM connects directly."
                color="blue"
            >
                {/* PVE Hosts */}
                <div className="mb-3">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Proxmox Hosts ({cluster.nodes.length})
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {controlNodes.map((n) => (
                            <Box
                                key={n.name}
                                icon={Server}
                                title={n.name}
                                sub={`control • ${n.underlay_ip || "no-ip"}`}
                                color="blue"
                                status={n.online ? "online" : "offline"}
                                testid={`topo-pve-${n.name}`}
                                onClick={() => setDetail({ title: `PVE — ${n.name}`, data: n })}
                            />
                        ))}
                        {workerNodes.map((n) => (
                            <Box
                                key={n.name}
                                icon={Server}
                                title={n.name}
                                sub={`worker • ${n.underlay_ip || "no-ip"}`}
                                color="zinc"
                                status={n.online ? "online" : "offline"}
                                testid={`topo-pve-${n.name}`}
                                onClick={() => setDetail({ title: `PVE — ${n.name}`, data: n })}
                            />
                        ))}
                    </div>
                </div>

                {/* DB Pools */}
                {data.db_pools.length > 0 && (
                    <div className="mb-3">
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                            MariaDB Pools ({data.db_pools.length})
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {data.db_pools.flatMap((p) =>
                                p.members.map((m) => (
                                    <Box
                                        key={`${p.id}-${m.host}`}
                                        icon={Database}
                                        title={`${p.name}/${m.role || "?"}`}
                                        sub={m.host}
                                        color="green"
                                        status={m.status}
                                        testid={`topo-db-${m.host}`}
                                        onClick={() =>
                                            setDetail({ title: `DB — ${p.name}/${m.host}`, data: { pool: p, member: m } })
                                        }
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* S3 Pools */}
                {data.s3_pools.length > 0 && (
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                            Object Storage ({data.s3_pools.length})
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {data.s3_pools.flatMap((p) =>
                                p.members.map((m) => (
                                    <Box
                                        key={`${p.id}-${m.endpoint_url}`}
                                        icon={HardDrive}
                                        title={p.name}
                                        sub={m.endpoint_url?.replace(/^https?:\/\//, "")}
                                        color="green"
                                        status={m.status}
                                        testid={`topo-s3-${m.endpoint_url}`}
                                        onClick={() =>
                                            setDetail({ title: `S3 — ${p.name}`, data: { pool: p, member: m } })
                                        }
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}
            </Lane>

            <Arrow
                label={
                    evpnReady
                        ? `EVPN VRF (${evpnZone.zone}, vrf-vxlan ${evpnZone.vrf_vxlan}) — leaks to ${cluster.underlay_bridge} via exit-nodes`
                        : "(no VRF — bootstrap EVPN to enable backend-LAN access from VMs)"
                }
            />

            {/* ===== LAYER 4: EVPN Overlay (Service + Tenant Zones) ===== */}
            <Lane
                label="EVPN Overlay (VXLAN + iBGP via FRR)"
                sublabel={
                    evpnReady
                        ? `controller=${evpnZone.controller} • asn=${cluster.sdn_asn} • exit-nodes=${evpnZone.exitnodes.join(", ") || "—"}`
                        : `Not yet active. Single-VXLAN fallback in use.`
                }
                color="purple"
            >
                {/* Service-Zone */}
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Network size={14} className="text-purple-300" />
                        <div className="text-xs uppercase tracking-wider text-purple-200 font-semibold">
                            Service-Zone {serviceVnet ? `(${serviceVnet.vnet}, ${serviceVnet.subnet})` : "(cmsvc0)"}
                        </div>
                        {serviceVnet?.gateway && (
                            <code className="text-[11px] text-zinc-400">
                                anycast-gw {serviceVnet.gateway}
                            </code>
                        )}
                    </div>
                    {cluster.service_vms.length === 0 ? (
                        <div className="text-xs text-zinc-500 italic pl-6">
                            No service VMs provisioned yet.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 pl-6">
                            {cluster.service_vms.map((v) => (
                                <Box
                                    key={v.id}
                                    icon={Cpu}
                                    title={v.hostname || `${v.kind}-${v.vmid}`}
                                    sub={`${v.kind} • ${v.ip_internal || "no-ip"} • @${v.node}`}
                                    color="purple"
                                    status={v.health || v.status}
                                    testid={`topo-svc-${v.vmid}`}
                                    onClick={() =>
                                        setDetail({ title: `Service VM — ${v.hostname}`, data: v })
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Tenant Zones — echte Werte, Live-Health, Tenant-Trennung */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Boxes size={14} className="text-purple-300" />
                        <div className="text-xs uppercase tracking-wider text-purple-200 font-semibold">
                            Tenant Zones ({cluster.tenant_subnets.length})
                        </div>
                        <span className="text-[11px] text-zinc-500 font-mono">— per-tenant subnet/VNet (isolated)</span>
                    </div>
                    <TenantZones
                        tenants={cluster.tenant_subnets || []}
                        summary={cluster.tenant_summary}
                        onDetail={setDetail}
                    />
                </div>
            </Lane>

            {/* Legend */}
            <div className="cm-card p-4">
                <div className="cm-h3 mb-2 flex items-center gap-2">
                    <Eye size={14} /> Legend
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-zinc-400">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded border-2 border-[#CCFF00]/60 bg-[#CCFF00]/5" />
                        Physical / Backend LAN
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded border-2 border-purple-700 bg-purple-950/30" />
                        EVPN Overlay
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded border-2 border-amber-700 bg-amber-950/30" />
                        Egress Gateway
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded border-2 border-emerald-700 bg-emerald-950/30" />
                        Data Stores (DB / S3)
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusDot status="active" /> healthy / active
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusDot status="provisioning" /> in-progress
                    </div>
                    <div className="flex items-center gap-2">
                        <StatusDot status="error" /> error / offline
                    </div>
                </div>
            </div>

            {detail && (
                <DetailDrawer
                    open={!!detail}
                    onClose={() => setDetail(null)}
                    title={detail.title}
                    data={detail.data}
                />
            )}
        </div>
    );
}
